import { Button, Card, Heading, ProgressIndicator, Tag } from '@boltenergy-be/design-system'
import { ProducerCardProps, ProducerStatus } from 'components/producer-card/types'
import { getStatus } from 'components/producer-card/utils'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import styles from './ProducerCard.module.scss'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import classNames from 'classnames'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { setSelectedProducer } from 'store/app/slice'
import { useContext } from 'react'
import { ProducerModalContext } from 'features/producer-detail/context'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { getTotalSimulatedPrice } from 'utils/price'
import { formatAmount } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import parse from 'html-react-parser'
import { getEnergyTypeIcon } from 'utils/producers'
import { SIMULATION_TYPES, SimulationType } from '@boltenergy-be/shared'

const ProducerCard = ({
  active,
  className,
  hideButton,
  hideDescription,
  onButtonClick,
  onMouseEnter,
  onMouseLeave,
  producer
}: ProducerCardProps) => {
  // Redux store
  const { flows, inputs, propositions } = useStoreSelector((store) => store.customer)
  const { isLoading, didSimulation } = flows.simulation
  const dispatch = useStoreDispatch()

  // Context (from ProducerDetail)
  const { setProducerModalOpen } = useContext(ProducerModalContext)

  // Router
  const router = useRouter()

  // i18n
  const { t } = useTranslation(['producer', 'common'])
  const language = useGetLanguage()

  // Constants
  const parentProducerRoute = routes(language, producer.parentSlug).producerInfo
  const producerStatus = getStatus(producer, language)
  const simulationCompleted = didSimulation || isLoading
  const showPrice =
    inputs.simulation.chosenSimulationType === SIMULATION_TYPES.pricingSimulation &&
    simulationCompleted &&
    propositions?.selectedProposition

  /**
   * Removes scroll lock on body
   */
  const removeScrollLock = () => {
    document.body.classList.remove('no-scroll')
  }

  /**
   * Handles the default button click
   *
   * @param {MouseEvent<HTMLButtonElement>} e
   */
  const defaultOnButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()

    // Set the selected producer
    dispatch(setSelectedProducer({ producerSlug: producer.slug }))

    // Close producer modal
    if (setProducerModalOpen) {
      setProducerModalOpen(false)
    }

    // Redirect to registration if simulation already completed
    if (
      simulationCompleted ||
      !([SIMULATION_TYPES.pricingSimulation, SIMULATION_TYPES.express] as SimulationType[]).includes(inputs.simulation.chosenSimulationType)
    ) {
      removeScrollLock()
      return router.push(routes(language, producer.slug).registration)
    }

    // Start simulation
    startSimulationOrRegistration()
  }

  return (
    <Card
      className={classNames(styles['producer-card'], { [styles.active]: active }, className)}
      color="white"
      image={{ alt: producer.name[language], position: 'top', src: producer.images.profile?.url, className: styles.image }}
      onMouseEnter={() => onMouseEnter && onMouseEnter(producer)}
      onMouseLeave={() => onMouseLeave && onMouseLeave()}
    >
      {producerStatus && (
        <Tag className={styles['status']} type="dark" size="small">
          {t(`producer:status.${producerStatus}`)}
        </Tag>
      )}

      <Tag type="positive" icon={getEnergyTypeIcon(producer.energyType)} size="small" className={styles['energy-source-tag']}>
        {t(`producer:energyTypes.${producer.energyType}`)} {t('common:from')} {producer.location[language]}
      </Tag>

      <Heading as="h2" variant="h4" serif>
        {producer.name[language]}
      </Heading>

      {!hideDescription && (
        <>
          <p>{producer.description[language]}</p>

          <Link href={parentProducerRoute} onNavigate={removeScrollLock}>
            {t('producer:readMore')}
          </Link>
        </>
      )}

      <ProgressIndicator
        id={`producer-capacity-${producer.id}`}
        label={t('producer:progressLabel')}
        progress={Math.min(Number(producer.currentCapacity.toFixed(0)), 100)}
        className="mt-auto"
      />

      {hideDescription && hideButton && (
        <Link representation="button" isFullwidth size="small" variant="secondary" href={parentProducerRoute} onNavigate={removeScrollLock}>
          {t('producer:discover')}
        </Link>
      )}

      {!hideButton && (
        <Button
          disabled={[ProducerStatus.RESERVED, ProducerStatus.SOLD_OUT].includes(producerStatus)}
          isFullwidth
          loading={isLoading}
          onClick={onButtonClick || defaultOnButtonClick}
          size={showPrice ? 'small' : 'medium'}
          className={styles.button}
        >
          {showPrice
            ? parse(t('producer:simulatedButton', { price: formatAmount(getTotalSimulatedPrice(), language) }))
            : t('common:choose')}
        </Button>
      )}
    </Card>
  )
}

export default ProducerCard
